import React, { useRef, useState, useEffect } from 'react';

export default function Tabs({ dates, selected, onSelect }) {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollStart, setScrollStart] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);

  // Update thumb size and position
  useEffect(() => {
    const container = scrollRef.current;
    const track = trackRef.current;
    if (!container || !track) return;

    function updateThumb() {
      const { scrollWidth, clientWidth, scrollLeft } = container;
      const trackWidth = track.clientWidth;
      const newThumbWidth = (clientWidth / scrollWidth) * trackWidth;
      const maxThumbLeft = trackWidth - newThumbWidth;
      const newThumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * maxThumbLeft;
      setThumbWidth(newThumbWidth);
      setThumbLeft(newThumbLeft);
    }

    updateThumb();
    container.addEventListener('scroll', updateThumb);
    window.addEventListener('resize', updateThumb);

    return () => {
      container.removeEventListener('scroll', updateThumb);
      window.removeEventListener('resize', updateThumb);
    };
  }, [dates]);

  // Mouse wheel for horizontal scroll
  const onWheel = (e) => {
    e.preventDefault();
    scrollRef.current.scrollLeft += e.deltaY;
  };

  // Drag-to-scroll
  const onMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollStart(scrollRef.current.scrollLeft);
  };
  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = x - startX;
    scrollRef.current.scrollLeft = scrollStart - walk;
  };
  const onMouseUp = () => setIsDragging(false);

  // Thumb drag
  const onThumbMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    const trackRect = trackRef.current.getBoundingClientRect();
    setStartX(e.pageX - trackRect.left - thumbLeft);
  };
  const onThumbMouseMove = (e) => {
    if (!isDragging) return;
    const container = scrollRef.current;
    const track = trackRef.current;
    const trackRect = track.getBoundingClientRect();
    const x = e.pageX - trackRect.left - startX;
    const { scrollWidth, clientWidth } = container;
    const maxThumbLeft = trackRect.width - thumbWidth;
    const newLeft = Math.max(0, Math.min(x, maxThumbLeft));
    const newScrollLeft = (newLeft / maxThumbLeft) * (scrollWidth - clientWidth);
    container.scrollLeft = newScrollLeft;
  };

  // Attach global events when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.addEventListener('mousemove', onThumbMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    } else {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onThumbMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onThumbMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="relative group">
      <div
        ref={scrollRef}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        className="flex flex-nowrap space-x-2 p-4 bg-gray-100 dark:bg-gray-700 overflow-x-auto no-scrollbar cursor-grab select-none"
      >
        {dates.map(d => {
          const [year, month, day] = d.split('-');
          const displayDate = `${month}/${day}/${year}`;
          return (
            <button
              key={d}
              onClick={() => onSelect(d)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg border transition-colors duration-200 select-none cursor-pointer ${
                selected === d
                  ? 'bg-blue-500 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-600'
              }`}
            >
              {displayDate}
            </button>
          );
        })}
      </div>

      {/* Custom scrollbar track, shown on hover only */}
      <div
        ref={trackRef}
        className="absolute bottom-0 inset-x-4 h-1 bg-gray-300 dark:bg-gray-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <div
          onMouseDown={onThumbMouseDown}
          className="h-1 bg-blue-500 rounded-full cursor-pointer"
          style={{ width: thumbWidth, transform: `translateX(${thumbLeft}px)` }}
        />
      </div>
    </div>
  );
}